// @ts-check

import { AlertTriangle, Download, Tag } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminInventoryService from '../services/adminInventoryService';
import api from '../utils/apiClient';
import { STOREFRONT_URL as SITE_URL } from '../utils/storefront';
import { buildTagCsv } from '../utils/tagExport';

/**
 * Hanging-tag export for the Inventory "All" tab. `picks` maps variant_id to the variant row and
 * how many tags to print; the CSV goes to the tag printer, whose QR codes point at each product page.
 *
 * Exporting asks which country the tags are for (admin-managed list, GET
 * /api/admin/pricing/country-config - the same list PricingConfigPage manages): the on-screen
 * summary uses the already-loaded, un-converted variant data, but the actual export re-fetches
 * just the picked variants with that country's currency-converted price and its URL slug, so the
 * printed price and QR link both match what that country's customers actually see.
 * @param {{
 *   picks: Record<string, {variant: any, copies: number}>,
 *   visibleCount: number,
 *   onOnePerVisible: () => void,
 *   onClear: () => void,
 * }} props
 */
const TagExportBar = ({ picks, visibleCount, onOnePerVisible, onClear }) => {
  const { showToast } = useToast();
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .get('/api/admin/pricing/country-config')
      .then((data) => setCountries(data.filter((c) => c.is_active && c.slug && c.currency)))
      .catch(() => {}); // silent - export just stays disabled without a country to pick
  }, []);

  const selected = Object.values(picks)
    .filter((p) => p.copies > 0)
    .map((p) => ({ ...p.variant, copies: p.copies }));
  // Preview only (count/issues) - built from the already-loaded, un-converted data so it doesn't
  // need a country picked yet; the actual export rebuilds this with converted prices below.
  const { count, issues } = buildTagCsv(selected, SITE_URL);

  const handleExport = async () => {
    const country = countries.find((c) => c.country_code === countryCode);
    if (!country) return;

    setExporting(true);
    try {
      const { items } = await adminInventoryService.getAllVariants({
        variantIds: selected.map((v) => v.variant_id).join(','),
        currency: country.currency,
        country: country.country_code,
      });
      const convertedById = Object.fromEntries(items.map((item) => [item.variant_id, item]));
      const enriched = selected.map((v) => ({ ...v, ...convertedById[v.variant_id] }));

      const { csv, count: exportedCount } = buildTagCsv(enriched, SITE_URL, country.slug);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kaay-tags-${country.slug}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      showToast(`Exported ${exportedCount} tags for ${country.country_name}`, 'success');
    } catch (err) {
      showToast(err.message || 'Could not export tags for that country', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mb-6 bg-card border border-border rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Tag size={18} className="text-muted-foreground" />
        <span className="text-sm text-foreground/80">
          <strong>{count}</strong> tags across <strong>{selected.length}</strong> variants. Enter a count in the Tags
          column of each row.
        </span>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <button
            onClick={onOnePerVisible}
            disabled={visibleCount === 0}
            className="px-3 py-1.5 border border-input rounded-md text-xs hover:bg-muted/50 disabled:opacity-50"
          >
            1 tag for each row shown
          </button>
          <button
            onClick={onClear}
            disabled={count === 0}
            className="px-3 py-1.5 border border-input rounded-md text-xs hover:bg-muted/50 disabled:opacity-50"
          >
            Clear
          </button>
          <select
            aria-label="Country"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="px-2 py-1.5 border border-input rounded-md text-xs"
          >
            <option value="">Country...</option>
            {countries.map((c) => (
              <option key={c.country_code} value={c.country_code}>
                {c.country_name}
              </option>
            ))}
          </select>
          <button
            onClick={handleExport}
            disabled={count === 0 || !SITE_URL || !countryCode || exporting}
            className="inline-flex items-center px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs hover:bg-primary/90 disabled:opacity-50"
          >
            <Download size={13} className="mr-1" /> {exporting ? 'Exporting...' : 'Export CSV for tag printer'}
          </button>
        </div>
      </div>

      {!SITE_URL && (
        <p className="mt-3 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertTriangle size={13} /> REACT_APP_STOREFRONT_URL is not set, so the QR links cannot be built. Set it to
          the storefront origin and restart or redeploy.
        </p>
      )}

      {issues.length > 0 && (
        <ul className="mt-3 text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
          {issues.map((issue, i) => (
            <li key={i} className="flex items-center gap-1">
              <AlertTriangle size={13} /> {issue.product_name}
              {issue.size ? ` (${issue.size})` : ''}: {issue.problem}. That tag will print with a blank field.
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TagExportBar;
